/**
 * File Slice
 *
 * Fetches executions matching file names via POST /files/fetch.
 * Server-side paginated — the page re-dispatches fetchFiles on page changes.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { FetchFilesResponse, fileInitialState } from './fileState';
import { beaconApi } from 'src/services/beaconApi';
import type { RootState } from 'src/store/store';

export interface FetchFilesArgs {
  fileNames: string[];
  pageNumber: number;
  pageSize: number;
}

/** Fetches one page of executions matching the given file names. */
export const fetchFiles = createAsyncThunk<
  FetchFilesResponse, FetchFilesArgs, { state: RootState; rejectValue: string }
>('file/fetch', async ({ fileNames, pageNumber, pageSize }, { getState, rejectWithValue }) => {
  try {
    const { apiBase } = getState().env;
    return await beaconApi.fetchFiles(fileNames, pageNumber, pageSize, apiBase);
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch files');
  }
});

const fileSlice = createSlice({
  name: 'file',
  initialState: fileInitialState,
  reducers: {
    clearFile: () => fileInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFiles.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return; // stale-request guard
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return; // stale-request guard
        state.loading = false;
        state.error = action.payload || 'Failed to fetch files';
      });
  },
});

export const { clearFile } = fileSlice.actions;
export default fileSlice.reducer;
